import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Sheet, RefreshCw, Save, Copy, ExternalLink, CheckCircle2, XCircle, AlertCircle } from "lucide-react";

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

export default function GoogleSheetSyncPage() {
  const [cfg, setCfg] = useState(null);
  const [logs, setLogs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // Local form state
  const [sheetUrl, setSheetUrl] = useState("");
  const [sheetName, setSheetName] = useState("");
  const [autoSync, setAutoSync] = useState(false);
  const [processBg, setProcessBg] = useState(false);

  const loadAll = async () => {
    try {
      const [c, l] = await Promise.all([
        api.get("/admin/products/gsheet/config"),
        api.get("/admin/products/gsheet/logs"),
      ]);
      setCfg(c.data);
      setLogs(l.data || []);
      setSheetUrl(c.data.sheet_url || "");
      setSheetName(c.data.sheet_name || "");
      setAutoSync(!!c.data.auto_sync);
      setProcessBg(!!c.data.process_bg);
    } catch (e) {
      toast.error("Errore caricamento configurazione");
    }
  };

  useEffect(() => { loadAll(); }, []);

  const saveConfig = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/products/gsheet/config", {
        sheet_url: sheetUrl,
        sheet_name: sheetName,
        auto_sync: autoSync,
        process_bg: processBg,
      });
      setCfg({ ...cfg, ...data });
      toast.success("Configurazione salvata");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const runSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/products/gsheet/sync");
      toast.success(`Sync ok — ${data.created} nuovi, ${data.updated} aggiornati, ${data.skipped} saltati`);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync fallita");
      await loadAll();
    } finally {
      setSyncing(false);
    }
  };

  const copyEmail = () => {
    if (!cfg?.service_account_email) return;
    navigator.clipboard.writeText(cfg.service_account_email);
    toast.success("Email service account copiata");
  };

  if (!cfg) return <div className="p-8 text-zinc-400">Caricamento…</div>;

  const saConfigured = !!cfg.service_account_email;

  return (
    <div data-testid="gsheet-sync-page" className="p-6 lg:p-8 space-y-8 max-w-5xl">
      <div>
        <div className="flex items-center gap-3 mb-1">
          <Sheet className="w-6 h-6 text-yellow-400" strokeWidth={2.5} />
          <h1 className="font-heading uppercase text-3xl text-white">Google Sheets Sync</h1>
        </div>
        <p className="text-zinc-400 text-sm max-w-2xl">
          Collega un Google Sheet al catalogo. Le righe vengono importate con la stessa logica UPSERT del bulk import (chiave: SKU).
          Il sync automatico gira una volta ogni ora quando attivo.
        </p>
      </div>

      {/* Step 1: Service Account status */}
      <section className="border-2 border-zinc-800 p-6 bg-zinc-900/40">
        <div className="flex items-center justify-between mb-4">
          <div className="font-heading uppercase text-yellow-400 tracking-widest text-sm">1. Service Account</div>
          {saConfigured ? (
            <span data-testid="gsheet-sa-status-ok" className="flex items-center gap-1 text-emerald-400 text-xs font-mono uppercase">
              <CheckCircle2 className="w-4 h-4" /> Configurato
            </span>
          ) : (
            <span data-testid="gsheet-sa-status-missing" className="flex items-center gap-1 text-red-400 text-xs font-mono uppercase">
              <XCircle className="w-4 h-4" /> Mancante
            </span>
          )}
        </div>

        {saConfigured ? (
          <div className="space-y-2">
            <div className="text-xs uppercase tracking-widest text-zinc-500">Email service account (condividi il foglio con questa email, ruolo Viewer)</div>
            <div className="flex items-center gap-2">
              <code data-testid="gsheet-sa-email" className="flex-1 bg-black border border-zinc-700 px-3 py-2 text-sm text-yellow-400 font-mono truncate">
                {cfg.service_account_email}
              </code>
              <button data-testid="gsheet-sa-copy" onClick={copyEmail} className="px-3 py-2 border-2 border-zinc-700 hover:border-yellow-400 text-zinc-300 hover:text-yellow-400 transition">
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </div>
        ) : (
          <div className="text-sm text-zinc-400 space-y-3">
            <p>Per attivare il sync serve un service account Google con accesso al foglio:</p>
            <ol className="list-decimal list-inside space-y-1 text-zinc-300 text-sm">
              <li>Vai su <a href="https://console.cloud.google.com/iam-admin/serviceaccounts" target="_blank" rel="noreferrer" className="text-yellow-400 hover:underline inline-flex items-center gap-1">Google Cloud Console → Service Accounts <ExternalLink className="w-3 h-3" /></a></li>
              <li>Crea un service account e scarica la sua chiave JSON</li>
              <li>Abilita <strong>Google Sheets API</strong> nel progetto</li>
              <li>Incolla l&apos;intero contenuto del JSON nella variabile <code className="bg-black px-1 text-yellow-400">GOOGLE_SERVICE_ACCOUNT_JSON</code> in <code className="bg-black px-1 text-yellow-400">/app/backend/.env</code> e riavvia il backend</li>
              <li>Torna qui: comparirà l&apos;email del service account da condividere sul foglio</li>
            </ol>
          </div>
        )}
      </section>

      {/* Step 2: Config */}
      <section className="border-2 border-zinc-800 p-6 bg-zinc-900/40 space-y-4">
        <div className="font-heading uppercase text-yellow-400 tracking-widest text-sm">2. Configurazione foglio</div>

        <div>
          <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">URL Google Sheet</label>
          <input
            data-testid="gsheet-input-url"
            type="url"
            value={sheetUrl}
            onChange={(e) => setSheetUrl(e.target.value)}
            placeholder="https://docs.google.com/spreadsheets/d/…"
            className="w-full bg-black border-2 border-zinc-700 focus:border-yellow-400 text-white px-3 py-2 outline-none font-mono text-sm"
          />
        </div>

        <div>
          <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Nome foglio (opzionale)</label>
          <input
            data-testid="gsheet-input-sheet-name"
            type="text"
            value={sheetName}
            onChange={(e) => setSheetName(e.target.value)}
            placeholder="Prodotti (default: primo foglio disponibile)"
            className="w-full bg-black border-2 border-zinc-700 focus:border-yellow-400 text-white px-3 py-2 outline-none font-mono text-sm"
          />
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <label className="flex items-center gap-2 cursor-pointer text-zinc-300">
            <input
              type="checkbox"
              data-testid="gsheet-toggle-autosync"
              checked={autoSync}
              onChange={(e) => setAutoSync(e.target.checked)}
              className="w-4 h-4 accent-yellow-400"
            />
            <span className="font-heading uppercase tracking-widest text-xs">Sync automatico ogni ora</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer text-zinc-300">
            <input
              type="checkbox"
              data-testid="gsheet-toggle-processbg"
              checked={processBg}
              onChange={(e) => setProcessBg(e.target.checked)}
              className="w-4 h-4 accent-yellow-400"
            />
            <span className="font-heading uppercase tracking-widest text-xs">Elabora sfondo immagini (AI)</span>
          </label>
        </div>

        <div className="flex flex-wrap gap-3 pt-2">
          <button
            data-testid="gsheet-save-config"
            onClick={saveConfig}
            disabled={saving}
            className="flex items-center gap-2 bg-transparent border-2 border-zinc-600 hover:border-yellow-400 text-zinc-300 hover:text-yellow-400 font-heading uppercase tracking-widest text-xs px-4 py-2.5 transition disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? "Salvo…" : "Salva configurazione"}
          </button>
          <button
            data-testid="gsheet-sync-now"
            onClick={runSync}
            disabled={syncing || !sheetUrl}
            title={!sheetUrl ? "Inserisci prima l'URL del foglio" : (!saConfigured ? "Il Service Account non è configurato: il tentativo verrà loggato come errore" : "")}
            className="flex items-center gap-2 bg-yellow-400 text-black hover:bg-black hover:text-yellow-400 border-2 border-yellow-400 font-heading uppercase tracking-widest text-xs px-4 py-2.5 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Sync in corso…" : "Sync ora"}
          </button>
        </div>
      </section>

      {/* Step 3: Last sync */}
      <section className="border-2 border-zinc-800 p-6 bg-zinc-900/40">
        <div className="font-heading uppercase text-yellow-400 tracking-widest text-sm mb-4">3. Ultimo sync</div>
        {cfg.last_sync_at ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Data" value={formatDate(cfg.last_sync_at)} testId="gsheet-last-sync-date" />
            <StatCard label="Stato" value={cfg.last_sync_status || "—"} testId="gsheet-last-sync-status" highlight={cfg.last_sync_status === "success"} />
            <StatCard label="Aggiornati" value={cfg.last_sync_summary?.updated ?? "—"} testId="gsheet-last-sync-updated" />
            <StatCard label="Nuovi" value={cfg.last_sync_summary?.created ?? "—"} testId="gsheet-last-sync-created" />
          </div>
        ) : (
          <div className="text-zinc-500 text-sm italic">Nessun sync ancora eseguito.</div>
        )}
      </section>

      {/* Step 4: Logs */}
      <section className="border-2 border-zinc-800 p-6 bg-zinc-900/40">
        <div className="font-heading uppercase text-yellow-400 tracking-widest text-sm mb-4">4. Storico sync</div>
        {logs.length === 0 ? (
          <div className="text-zinc-500 text-sm italic">Nessun log disponibile.</div>
        ) : (
          <div className="overflow-x-auto">
            <table data-testid="gsheet-logs-table" className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-800">
                  <th className="py-2 pr-4">Data</th>
                  <th className="py-2 pr-4">Origine</th>
                  <th className="py-2 pr-4">Stato</th>
                  <th className="py-2 pr-4">Nuovi</th>
                  <th className="py-2 pr-4">Aggiornati</th>
                  <th className="py-2 pr-4">Saltati</th>
                  <th className="py-2">Dettagli</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id} className="border-b border-zinc-900 hover:bg-zinc-900/30">
                    <td className="py-3 pr-4 font-mono text-zinc-400 whitespace-nowrap">{formatDate(log.at)}</td>
                    <td className="py-3 pr-4 text-zinc-400 uppercase text-xs">{log.trigger}</td>
                    <td className="py-3 pr-4">
                      {log.status === "success" ? (
                        <span className="inline-flex items-center gap-1 text-emerald-400 text-xs font-mono uppercase">
                          <CheckCircle2 className="w-3 h-3" /> ok
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-red-400 text-xs font-mono uppercase">
                          <AlertCircle className="w-3 h-3" /> errore
                        </span>
                      )}
                    </td>
                    <td className="py-3 pr-4 text-white">{log.summary?.created ?? "—"}</td>
                    <td className="py-3 pr-4 text-white">{log.summary?.updated ?? "—"}</td>
                    <td className="py-3 pr-4 text-white">{log.summary?.skipped ?? "—"}</td>
                    <td className="py-3 text-zinc-500 text-xs max-w-md truncate" title={log.summary?.error || (log.summary?.warnings || []).join(" · ")}>
                      {log.summary?.error || (log.summary?.warnings || []).slice(0, 2).join(" · ") || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

const StatCard = ({ label, value, testId, highlight }) => (
  <div className="border border-zinc-800 p-3 bg-black/40">
    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{label}</div>
    <div data-testid={testId} className={`font-mono text-sm ${highlight ? "text-emerald-400" : "text-white"}`}>{value}</div>
  </div>
);
